import { useMemo } from 'react'
import { Lock, Users } from 'lucide-react'
import { Label } from '@/components/ui/label'
import { SearchSelect } from '@/components/composites/search-select'
import type { TeamNode } from '@/types/entities'
import { flattenTeams } from '@/features/teams'

/**
 * Who can see this.
 *
 * One control for work items, projects and requests, because the rule is the same for all
 * three and three copies of it would drift.
 *
 * Empty means everyone, and the copy says so plainly rather than leaving somebody to wonder
 * whether an untouched field has quietly locked something. Naming anybody makes it private —
 * there is no separate toggle, because a toggle with an empty list underneath it is a state
 * that promises privacy and delivers none.
 */
export function VisibilityField({
  userIds,
  teamIds,
  onChange,
  members,
  teams,
  disabled = false,
}: {
  userIds: string[]
  teamIds: string[]
  onChange: (next: { userIds: string[]; teamIds: string[] }) => void
  members: Array<{ id: string; displayName: string; photoUrl?: string | null; disabled?: boolean }>
  teams: TeamNode[]
  disabled?: boolean
}) {
  const flat = useMemo(() => flattenTeams(teams), [teams])
  const restricted = userIds.length > 0 || teamIds.length > 0

  const people = useMemo(
    () =>
      members
        .filter((m) => !m.disabled)
        .map((m) => ({ id: m.id, label: m.displayName, photoUrl: m.photoUrl ?? null, group: 'People' })),
    [members],
  )
  const teamOptions = useMemo(
    () => flat.map((t) => ({ id: t.id, label: t.label, group: 'Teams' })),
    [flat],
  )

  return (
    <div className="grid gap-1.5">
      <Label className="flex items-center gap-1.5">
        {restricted ? <Lock className="size-3.5 text-brand" /> : <Users className="size-3.5 text-text-muted" />}
        Who can see this
      </Label>

      <SearchSelect
        multiple
        value={[...userIds, ...teamIds]}
        onChange={(next) => {
          // One picker, two lists: which one an id belongs in is a fact about the id, not
          // something to ask the person to sort out with two separate boxes.
          const teamSet = new Set(teamOptions.map((t) => t.id))
          onChange({
            userIds: next.filter((id) => !teamSet.has(id)),
            teamIds: next.filter((id) => teamSet.has(id)),
          })
        }}
        options={[...people, ...teamOptions]}
        placeholder="Everyone"
        disabled={disabled}
        emptyHint="Nobody to choose from yet."
      />

      <p className="text-tiny text-text-muted">
        {restricted
          ? 'Only these people, everyone on these teams, and whoever is working on it can see it.'
          : 'Everyone in the workspace can see this. Name someone to keep it to them.'}
      </p>
    </div>
  )
}
